// Plan Slug → Görünen İsim + Açıklama (TR + EN)
// DB'deki plan başlıkları çoğu zaman İngilizce ("Starter", "Professional") kalır;
// bu utility UI'de dile göre insan dostu isim ve açıklama sağlar.

import { getCurrentLang } from './featureLabels';

const PLAN_META = {
  solo: {
    tr: {
      name: 'Solo',
      description: 'Tek kişilik kullanım için. Onay akışı yok, harcamaları doğrudan girip cari hesabınızı takip edin.',
    },
    en: {
      name: 'Solo',
      description: 'For single-user use. No approval flow — enter expenses directly and track your current account.',
    },
  },
  starter: {
    tr: {
      name: 'Başlangıç',
      description: '5 kullanıcıya kadar küçük ekipler için. Avans talep, onay ve kapama akışı.',
    },
    en: {
      name: 'Starter',
      description: 'For small teams up to 5 users. Advance request, approval and closing flow.',
    },
  },
  professional: {
    tr: {
      name: 'Profesyonel',
      description: '15 kullanıcıya kadar büyüyen ekipler için. Excel/PDF dışa aktarım, çoklu para birimi, API erişimi.',
    },
    en: {
      name: 'Professional',
      description: 'For growing teams up to 15 users. Excel/PDF export, multi-currency, API access.',
    },
  },
  enterprise: {
    tr: {
      name: 'Kurumsal',
      description: '50 kullanıcıya kadar büyük organizasyonlar için. Tüm özellikler, WhatsApp entegrasyonu, öncelikli destek.',
    },
    en: {
      name: 'Enterprise',
      description: 'For large organizations up to 50 users. All features, WhatsApp integration, priority support.',
    },
  },
  free_trial: {
    tr: {
      name: '14 Günlük Ücretsiz Deneme',
      description: 'Tam özellikli deneme sürümü. Kredi kartı gerekmez.',
    },
    en: {
      name: '14-Day Free Trial',
      description: 'Full-featured trial. No credit card required.',
    },
  },
};

/**
 * DB'den gelen plan başlığı yerine yerelleştirilmiş adı döner.
 * Bilinmeyen slug → fallbackTitle (API'den gelen orijinal isim).
 *
 * ÖNEMLİ: Eğer `fallbackTitle` (Ultra Admin'de yönetici tarafından yazılmış
 * özel isim) verildiyse, hardcoded çeviriler yerine ONA öncelik verilir.
 * Böylece Ultra Admin > Özellik Yönetimi'nde bir paketin adı değiştirildiğinde
 * o değişiklik uygulamanın tüm ekranlarına yansır.
 */
export function planName(slug, lang, fallbackTitle) {
  const dbName = (fallbackTitle && String(fallbackTitle).trim()) || '';
  const meta = PLAN_META[slug];
  const defaultI18n = meta ? (meta[lang || getCurrentLang()]?.name || meta.tr?.name) : '';

  // If Ultra Admin has customised the name (i.e. `dbName` diverges from the
  // hardcoded default i18n label), prefer the admin's customisation so the
  // rename propagates everywhere. Fall back to i18n → slug otherwise.
  if (dbName && dbName !== defaultI18n) return dbName;
  if (defaultI18n) return defaultI18n;
  return dbName || String(slug || '');
}

/**
 * Verilen `packages` listesinden slug'a karşılık gelen DB adını (Ultra
 * Admin'in girmiş olduğu özel isim) çıkarır ve `planName` ile birleştirir.
 * RegisterPage, Dashboard vb. bileşenler bu yardımcıyı kullanır.
 */
export function planNameFromPackages(slug, packages, lang, fallbackTitle) {
  if (!Array.isArray(packages) || packages.length === 0) {
    return planName(slug, lang, fallbackTitle);
  }
  const key = String(slug || '').toLowerCase();
  const found = packages.find(p =>
    String(p.code || '').toLowerCase() === key ||
    String(p.id || '').toLowerCase() === key ||
    String(p.slug || '').toLowerCase() === key
  );
  const dbTitle = found ? (found.title || found.name) : null;
  return planName(slug, lang, dbTitle || fallbackTitle);
}

/**
 * Planın yerelleştirilmiş açıklaması. Bilinmeyen slug → fallbackDesc veya boş.
 */
export function planDescription(slug, lang, fallbackDesc) {
  const l = lang || getCurrentLang();
  const meta = PLAN_META[slug];
  if (meta) return meta[l]?.description || meta.tr?.description || fallbackDesc || '';
  return fallbackDesc || '';
}

export default { planName, planDescription, planNameFromPackages };
